import { Form, Select, Switch, Typography } from 'antd';
import { useCallback, useContext, useEffect } from 'react';

import { WORKDAY_START_END_CODES } from '@app/components/OperatingHours/data/daysSENames';
import { NOperatingHours } from '@app/components/OperatingHours/OperatingHours.types';
import { ERROR_REQUIRED_FIELD } from '@app/constants';
import { useMemoizeObject } from '@app/hooks';
import { NotificationContext } from '@app/providers';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { correctTimeInterval } from '@app/utils';

import TData = NOperatingHours.Data;

export const OperatingHours: NOperatingHours.Main = ({
  form,
  setWorkStatus,
  dayWorkStatus,
  times,
  day,
  switchName,
  workDayStartCode,
  workDayEndCode,
  setNewData: setBranchState,
  newData: branchState,
}) => {
  const { showNotification } = useContext(NotificationContext);

  const memoizedTimes = useMemoizeObject(times);
  const memoizedBranchState = useMemoizeObject(branchState);

  const findTimeIndexByLabelName = useCallback(
    (globalLabel: string): number => {
      return times.findIndex(
        ({ label: localLabel }) => localLabel === globalLabel
      );
    },
    [times]
  );

  const findCloserWorkDay = useCallback(
    (day: string) => {
      const DEFAULT_START_TIME = '10:00';
      const DEFAULT_END_TIME = '22:00';
      const DEFAULT_START_TIME_INDEX =
        findTimeIndexByLabelName(DEFAULT_START_TIME);
      const DEFAULT_END_TIME_INDEX = findTimeIndexByLabelName(DEFAULT_END_TIME);
      const dayNameIndex = WORKDAY_START_END_CODES.indexOf(day);

      if (dayNameIndex === -1) {
        return;
      }

      if (day.endsWith('S')) {
        form.setFieldValue(day, DEFAULT_START_TIME_INDEX);
        setBranchState((prev: TData<typeof branchState>) => {
          return {
            ...prev,
            [day]: branchState[day],
          };
        });
      } else if (day.endsWith('E')) {
        form.setFieldValue(day, DEFAULT_END_TIME_INDEX);
        setBranchState((prev: TData<typeof branchState>) => {
          return {
            ...prev,
            [day]: branchState[day],
          };
        });
      }

      for (let i = dayNameIndex; i >= 0; i--) {
        branchState[WORKDAY_START_END_CODES[i]];
        if (
          (branchState[WORKDAY_START_END_CODES[i]] ||
            branchState[WORKDAY_START_END_CODES[i]] === 0) &&
          day[day.length - 1] ===
            WORKDAY_START_END_CODES[i][WORKDAY_START_END_CODES[i].length - 1]
        ) {
          form.setFieldValue(day, branchState[WORKDAY_START_END_CODES[i]]);
          setBranchState((prev: TData<typeof branchState>) => {
            return {
              ...prev,
              [day]: branchState[WORKDAY_START_END_CODES[i]],
            };
          });
          return;
        }
      }

      for (let i = dayNameIndex; i < WORKDAY_START_END_CODES.length; i++) {
        branchState[WORKDAY_START_END_CODES[i]];
        if (
          (branchState[WORKDAY_START_END_CODES[i]] ||
            branchState[WORKDAY_START_END_CODES[i]] === 0) &&
          day[day.length - 1] ===
            WORKDAY_START_END_CODES[i][WORKDAY_START_END_CODES[i].length - 1]
        ) {
          form.setFieldValue(day, branchState[WORKDAY_START_END_CODES[i]]);
          setBranchState((prev: TData<typeof branchState>) => {
            return {
              ...prev,
              [day]: branchState[WORKDAY_START_END_CODES[i]],
            };
          });
          return;
        }
      }
    },
    [branchState, form, findTimeIndexByLabelName, setBranchState]
  );

  useEffect(() => {
    if (memoizedBranchState && memoizedBranchState[switchName]) {
      findCloserWorkDay(workDayStartCode);
      findCloserWorkDay(workDayEndCode);
    }
    // Рекомендуемая линтером в качестве зависимости функция findCloserWorkDay
    // приводит к циклической перерисовке компонента OperatingHours
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchName, workDayStartCode, workDayEndCode, memoizedBranchState]);

  useEffect(() => {
    const inputStart = (memoizedTimes || []).find(
      ({ value }) => value === form.getFieldValue(workDayStartCode)
    );
    const inputEnd = (memoizedTimes || []).find(
      ({ value }) => value === form.getFieldValue(workDayEndCode)
    );

    if (inputStart && inputEnd) {
      correctTimeInterval({
        inputStart,
        inputEnd,
        showNotification,
        cbToSaveBothValues: ([{ value: startValue }, { value: endValue }]) => {
          form.setFieldValue(workDayStartCode, startValue);
          form.setFieldValue(workDayEndCode, endValue);

          setBranchState((prev: TData<typeof memoizedBranchState>) => {
            return {
              ...prev,
              [workDayStartCode]: startValue,
              [workDayEndCode]: endValue,
            };
          });
        },
      });
    }
  }, [
    form,
    workDayEndCode,
    workDayStartCode,
    memoizedBranchState,
    memoizedTimes,
    showNotification,
    setBranchState,
  ]);

  return (
    <div className="operating-hours-row">
      <Typography.Text className="operating-hours-row-title">
        {day}
      </Typography.Text>

      <Form.Item name={switchName} valuePropName="checked" className="switch">
        <Switch
          data-testid={form.getFieldValue(workDayStartCode) ? 'open' : 'close'}
          onChange={(value) => {
            findCloserWorkDay(workDayStartCode);
            findCloserWorkDay(workDayEndCode);

            if (!value) {
              form.resetFields([workDayStartCode, workDayEndCode]);
              setBranchState((prev: TData<typeof branchState>) => {
                return {
                  ...prev,
                  [switchName]: value,
                  [workDayStartCode]: undefined,
                  [workDayEndCode]: undefined,
                };
              });
            }
            setWorkStatus((prev: boolean) => !prev);
            setBranchState((prev: TData<typeof branchState>) => {
              return {
                ...prev,
                [switchName]: value,
              };
            });
          }}
        />
      </Form.Item>

      <Typography.Text className="operating-hours-row-status">
        Открыто
      </Typography.Text>

      <div className="time-field">
        <div className="time-input">
          <Form.Item
            name={workDayStartCode}
            rules={[
              {
                required: dayWorkStatus,
                message: ERROR_REQUIRED_FIELD,
              },
            ]}
          >
            <Select
              data-testid="start-work-time"
              size="large"
              options={times.map(({ label, value }) => {
                return {
                  label: generateSpanWithDataTestId(
                    label,
                    'start-work-time-item'
                  ),
                  value,
                };
              })}
              disabled={!dayWorkStatus}
              onChange={(value) => {
                setBranchState((prev: TData<typeof branchState>) => {
                  return {
                    ...prev,
                    [workDayStartCode]: value,
                  };
                });
              }}
            />
          </Form.Item>
        </div>
        -
        <div className="time-input">
          <Form.Item
            name={workDayEndCode}
            rules={[
              {
                required: dayWorkStatus,
                message: ERROR_REQUIRED_FIELD,
              },
            ]}
          >
            <Select
              data-testid="end-work-time"
              size="large"
              options={times.map(({ label, value }) => {
                return {
                  label: generateSpanWithDataTestId(
                    label,
                    'end-work-time-item'
                  ),
                  value,
                };
              })}
              disabled={!dayWorkStatus}
              onChange={(value) => {
                setBranchState((prev: TData<typeof branchState>) => {
                  return {
                    ...prev,
                    [workDayEndCode]: value,
                  };
                });
              }}
            />
          </Form.Item>
        </div>
      </div>
    </div>
  );
};
